import { NSnackbarProps } from '@/modules/global/components/core/NSnackbar/NSnackbar.vue'
import { defineStore } from 'pinia'
import { uuid } from 'vue-uuid'

type SnackbarState = { items: NSnackbarProps[] }

const autoHideTimers = new Map<string, number>()

function clearAutoHideTimer(id: string) {
  const timer = autoHideTimers.get(id)
  if (timer !== undefined) {
    window.clearTimeout(timer)
    autoHideTimers.delete(id)
  }
}

/**
 * The countdown only runs while the page is visible: a snackbar raised just before the user switches to another tab
 * (e.g. "Opened in new tab") would otherwise expire unseen. A hidden page restarts the full duration once it is back.
 */
function scheduleAutoHide(id: string, duration: number, remove: (id: string) => void) {
  clearAutoHideTimer(id)
  const timer = window.setTimeout(() => {
    autoHideTimers.delete(id)
    if (!document.hidden) {
      remove(id)
      return
    }
    const onVisible = () => {
      if (document.hidden) return
      document.removeEventListener('visibilitychange', onVisible)
      scheduleAutoHide(id, duration, remove)
    }
    document.addEventListener('visibilitychange', onVisible)
  }, duration)
  autoHideTimers.set(id, timer)
}

export const useSnackbarStore = defineStore('snackbar', {
  state: (): SnackbarState => ({
    items: []
  }),
  getters: {
    snackbarsBySpaceId() {
      return (spaceId: string) => this.items.filter(s => (s.meta?.spaceId ? spaceId.includes(s.meta?.spaceId) : false))
    }
  },
  actions: {
    enqueue(props: NSnackbarProps) {
      const { autoHide, autoHideDuration, anchorOrigin, dedupeKey } = props
      const shouldAutoHide = autoHide || autoHide === undefined || autoHideDuration !== undefined
      const duration = autoHideDuration || 5000

      if (!anchorOrigin) {
        props.anchorOrigin = { vertical: 'bottom', horizontal: 'center' }
      }

      if (dedupeKey) {
        const existing = this.items.find(i => i.dedupeKey === dedupeKey)
        if (existing?.id) {
          if (shouldAutoHide) {
            scheduleAutoHide(existing.id, duration, id => this.dequeue(id))
          }
          return existing.id
        }
      }

      const id = props.id ?? uuid.v4()
      this.items.push({ id, ...props })
      if (shouldAutoHide) {
        scheduleAutoHide(id, duration, snackbarId => this.dequeue(snackbarId))
      }
      return id
    },
    dequeue(id: string) {
      clearAutoHideTimer(id)
      this.items = this.items.filter(i => i.id !== id)
    },
    removeSpaceSnackbars(spaceId: string) {
      const snackbarBySpaceId = this.snackbarsBySpaceId(spaceId)
      snackbarBySpaceId.forEach(s => {
        if (s.id) {
          clearAutoHideTimer(s.id)
        }
      })
      this.items = this.items.filter(i => !snackbarBySpaceId.map(s => s.id).includes(i.id))
    }
  }
})

export type SnackbarStore = ReturnType<typeof useSnackbarStore>
